import React, {useCallback, useState} from 'react'
import qs from 'qs'
import {not, pickBy, startsWith} from 'ramda'
import {rejectEmpty} from '@modifi/utilities'

export type FilterOption = {
  label: string
  value: string
  children?: FilterOption[]
}

/** url query parameters a filter writes back into the location search string */
type FilterSearchParams = Record<string, string | string[] | null | undefined>

type FilterLocation = {
  search: string
  pathname?: string
  hash?: string
  state?: unknown
  key?: string
}

/**
 * Props a filter accepts from its surroundings and forwards untouched to `Filter`:
 * `closeDropdown` and `formRef` are injected by `withDropdown` when the filter is rendered
 * as dropdown content, the remaining ones are handed over to the rendered filter form.
 * Filters should spread these (`...restFilterProps`) instead of accepting arbitrary props.
 */
export type CommonFilterProps = {
  closeDropdown: () => void
  formRef: React.RefObject<{submit: () => void}>
  onReset?: (...args: unknown[]) => void
  onSubmit?: (...args: unknown[]) => void
  maxHeight?: number
  canSubmit?: boolean
}

type FilterProps = CommonFilterProps & {
  filterComponent: React.ComponentType<any>
  location: FilterLocation
  search: Record<string, any>
  // the spontaneous search filter returns the raw list of selected values instead of a
  // props object, everything else returns props for the rendered `filterComponent`
  getDefaultParams?: (search: Record<string, any>) => Record<string, unknown> | unknown[]
  // the value is whatever the rendered `filterComponent` emits (selected options, a date range,
  // a search string, ...), so every filter narrows it in its own `getSearchParams`
  getSearchParams: (value: any) => FilterSearchParams
  getResetParams?: () => Record<string, unknown>
  replaceUrl: (location: FilterLocation) => void
  // forwarded to the `@modifi/ui` filter form rendered as `filterComponent`
  options?: FilterOption[]
  minLength?: number
  onSearch?: (searchString: string) => Promise<unknown>
  preferFutureDates?: boolean
  numberOfMonths?: number
}

const Filter: React.FC<FilterProps> = ({
  filterComponent: FilterComponent,
  closeDropdown,
  formRef,
  search,
  getDefaultParams = () => ({}),
  getSearchParams,
  getResetParams = () => ({}),
  onReset = () => null,
  // onSubmit is destructured only to keep it out of restFormProps (it must not override the
  // internal handleSubmit passed to FilterComponent); it is intentionally unused here.
  // eslint-disable-next-line react/require-default-props
  onSubmit,
  replaceUrl,
  location,
  ...restFormProps
}) => {
  const [defaultParams, setDefaultParams] = useState(getDefaultParams(search))

  const handleSubmit = useCallback(
    (value: any) => {
      const searchParams = getSearchParams(value)
      replaceUrl({
        ...location,
        search: qs.stringify(
          rejectEmpty({
            ...(searchParams['filter.externalId']
              ? {
                  ...(pickBy(
                    (val: any, key: string) => not(startsWith('filter', key)),
                    search
                  ) as Record<string, any>),
                }
              : search),
            ...searchParams,
            page: 1,
          })
        ),
      })
      // in case of spotanious filtering with search we do not want to close the filte dropdown
      if (!searchParams['search.contextParameters.value']) closeDropdown()
    },
    [location, search, replaceUrl, closeDropdown, getSearchParams]
  )

  const handleReset = (...args: unknown[]) => {
    setDefaultParams(getResetParams())
    onReset(...args)
  }

  return (
    <FilterComponent
      onSubmit={handleSubmit}
      onReset={handleReset}
      onChange={handleSubmit}
      formRef={formRef}
      defaultParam={getDefaultParams(search)}
      {...defaultParams}
      {...restFormProps}
    />
  )
}

export default Filter
